import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'

import Image from 'next/image'

import {
    Baby,
    BadgeCheck,
    Brain,
    CakeSlice,
    Cat,
    Check,
    Footprints,
    GraduationCap
} from 'lucide-react'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { CtaBand } from '@/components/generals/cta-band'
import { BookingButton } from '@/components/generals/button-link'
import { SectionHeading } from '@/components/generals/section-heading'
import { HeroImage, HeroPill, PageHero } from '@/components/generals/page-hero'

interface IconItem {
    icon: LucideIcon
    text: string
}

const signs: Array<string> = [
    "You say yes before you've even checked your calendar.",
    "You're exhausted, but your brain won't turn off at night.",
    "You feel guilty resting when there's still something to do.",
    "You've thought about therapy but aren't sure your problems are “big enough.”"
]

const credentials: Array<IconItem> = [
    { icon: BadgeCheck, text: "Licensed Mental Health Counselor (LMHC), Washington" },
    { icon: GraduationCap, text: "MA in Clinical Mental Health Counseling" },
    { icon: Brain, text: "Trained in CBT and ACT (two well-studied, skills-based approaches for anxiety)" },
    { icon: Baby, text: "Perinatal mental health training" }
]

const funFacts: Array<IconItem> = [
    { icon: Footprints, text: "I walk the Capitol Lake loop most mornings, rain or shine." },
    { icon: Cat, text: "My cat, Biscuit, sometimes makes a cameo in telehealth sessions." },
    { icon: CakeSlice, text: "I've seen every episode of The Great British Bake Off at least twice." }
]

const About: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title='About Dana'
                description="Meet Dana Reyes, LMHC, a licensed therapist in Olympia, WA who helps women with anxiety, people-pleasing and burnout feel calm again, in person and online."
            />

            <PageHero
                eyebrow={<HeroPill>About Dana Reyes, LMHC</HeroPill>}
                title="Hi, I'm Dana. I Help Women Who Do It All Feel Calm Again."
                description={
                    <p>
                        I'm a licensed therapist in Olympia who keeps therapy relaxed, honest and useful in your real life.
                    </p>
                }
                actions={<BookingButton size='lg' />}
                media={
                    <HeroImage
                        src='/images/dana-portrait.png'
                        alt="Dana Reyes, a smiling therapist with short brown hair in a soft beige sweater"
                        imageClassName='object-top'
                    />
                }
            />

            <section id='who-i-work-with' className='bg-lavender py-20 sm:py-28'>
                <div className='mx-auto max-w-5xl px-5 sm:px-8'>
                    <SectionHeading
                        eyebrow="Who I work with"
                        title="Does This Sound Like You?"
                        align='center'
                        className='reveal'
                    />

                    <ul className='reveal mt-14 grid gap-5 sm:grid-cols-2'>
                        {signs.map((sign) => (
                            <li key={sign} className='flex gap-4 rounded-[1.5rem] bg-shell p-6 shadow-sm sm:p-7'>
                                <span className='grid size-9 shrink-0 place-items-center rounded-full bg-honey/40 text-plum'>
                                    <Check className='size-5' aria-hidden='true' />
                                </span>

                                <p className='text-lg leading-relaxed text-espresso'>
                                    {sign}
                                </p>
                            </li>
                        ))}
                    </ul>

                    <p className='reveal mx-auto mt-12 max-w-2xl text-center font-heading text-2xl leading-snug text-plum text-balance sm:text-[1.75rem]'>
                        If this sounds familiar, you're in the right place. Let me show you how I can help.
                    </p>
                </div>
            </section>

            <section id='my-story' className='py-20 sm:py-28'>
                <div className='mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-12 lg:gap-20'>
                    <div className='reveal lg:col-span-5'>
                        <div className='lg:sticky lg:top-28'>
                            <SectionHeading
                                eyebrow="My story"
                                title="Why I Started Fernwell"
                            />

                            <div className='relative mt-10 aspect-[4/5] max-w-md overflow-hidden rounded-[2rem] shadow-xl shadow-plum/10'>
                                <Image
                                    src='/images/services-office.png'
                                    alt="A calm corner with a white armchair and a leafy plant against a soft lavender wall"
                                    fill
                                    sizes='(min-width: 1024px) 34vw, (min-width: 640px) 28rem, 100vw'
                                    className='object-cover'
                                />
                            </div>
                        </div>
                    </div>

                    <div className='reveal space-y-6 text-lg leading-relaxed text-espresso-soft sm:text-xl sm:leading-relaxed lg:col-span-7 lg:pt-16'>
                        <p>
                            I was the reliable one for a long time. In grad school I worked two jobs, said yes to everyone and told myself I was fine. I wasn't. I was anxious, tired and snapping at the people I loved.
                        </p>

                        <p>
                            My own therapist taught me something simple: you can care about people and still have limits. Learning that changed my life, and it's a big part of why I do this work.
                        </p>

                        <figure aria-hidden='true' className='my-10 border-l-4 border-honey py-2 pl-6 sm:pl-8'>
                            <p className='font-heading text-3xl leading-snug text-plum sm:text-4xl'>
                                “You can care about people and still have limits.”
                            </p>
                        </figure>

                        <p>
                            After six years at a busy community agency, I wanted to give each client more time and a calmer space. So I opened Fernwell: one cozy office, evening telehealth for busy moms, and therapy that feels like a real conversation.
                        </p>

                        <p>
                            Now I get to give you what I needed back then: a place to set everything down, practical tools, and someone in your corner.
                        </p>
                    </div>
                </div>
            </section>

            <section id='how-i-can-help' className='bg-sand py-20 sm:py-28'>
                <div className='reveal mx-auto max-w-3xl px-5 text-center sm:px-8'>
                    <SectionHeading
                        eyebrow="How I can help"
                        title="What We'll Work On Together"
                        align='center'
                        intro={
                            <p>
                                You want to feel calm, sleep through the night and stop carrying everyone else's stress. Together we'll find what keeps you stuck and practice simple skills that fit your real schedule. Over time, most clients find they worry less, set limits with less guilt and finally have energy left for themselves.
                            </p>
                        }
                    />
                </div>
            </section>

            <section id='credentials-and-fun-facts' className='py-20 sm:py-28'>
                <div className='mx-auto grid max-w-7xl gap-8 px-5 sm:px-8 lg:grid-cols-2'>
                    <div className='reveal rounded-[2rem] border border-border bg-card p-8 sm:p-10'>
                        <div className='flex flex-wrap items-baseline justify-between gap-3'>
                            <h2 className='font-heading text-3xl text-plum sm:text-4xl'>
                                Credentials
                            </h2>

                            <p className='text-sm text-espresso-soft'>
                                (samples)
                            </p>
                        </div>

                        <ul className='mt-8 space-y-5'>
                            {credentials.map(({ icon: Icon, text }) => (
                                <li key={text} className='flex gap-4'>
                                    <span className='grid size-11 shrink-0 place-items-center rounded-full bg-lavender text-plum'>
                                        <Icon className='size-5' aria-hidden='true' />
                                    </span>

                                    <p className='self-center text-lg leading-snug text-espresso'>
                                        {text}
                                    </p>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className='reveal rounded-[2rem] bg-lavender p-8 sm:p-10'>
                        <h2 className='font-heading text-3xl text-plum sm:text-4xl'>
                            Fun Facts
                        </h2>

                        <ul className='mt-8 space-y-5'>
                            {funFacts.map(({ icon: Icon, text }) => (
                                <li key={text} className='flex gap-4'>
                                    <span className='grid size-11 shrink-0 place-items-center rounded-full bg-shell text-plum'>
                                        <Icon className='size-5' aria-hidden='true' />
                                    </span>

                                    <p className='self-center text-lg leading-snug text-espresso'>
                                        {text}
                                    </p>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            <CtaBand />
        </Layout>
    )
}

export default About
